import { Component, OnInit } from '@angular/core'
import { Workspace, isWorkspaceGlobal, Schema } from '@core/models'
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms'
import { ModalContainerComponent } from '@components-library/tb-modal-manager/modal-container-component/modal-container.component'
import { dirtyCheck } from '@ngneat/dirty-check-forms'
import { schemaIconByName } from '@shared/models/icons'
import { of } from 'rxjs'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { CommonDialogResultStatus } from '@components-library/tb-confirmation-popup/tb-confirmation-dialog.component'
import { TbButtonComponent } from '@components-library/tb-button/tb-button.component'
import { AccessSelectorComponent } from '@shared/access-selector/access-selector.component'
import { EnvDirective } from '@shared/env.directive'
import { TbOptionComponent } from '@components-library/tb-select/components/tb-option/tb-option.component'

import { TbSelectComponent } from '@components-library/tb-select/components/tb-select/tb-select.component'
import { ModalLayoutComponent } from '@components-library/tb-modal-manager/modal-layout/modal-layout.component'
import { TranslocoModule } from '@jsverse/transloco'
import { ModalLayoutButtonsComponent } from '@components-library/tb-modal-manager/modal-layout-buttons/modal-layout-buttons.component'
import { TbIconComponent } from '@components-library/tb-icon/tb-icon.component'
import { TbTooltipComponent } from '@components-library/tb-tooltip/tb-tooltip-component/tb-tooltip.component'

export interface RecordSettingsData {
    schemas: Schema[]
    workspaces: Workspace[]
    selectedWorkspacesGuid: string[]
    selectedSchemaGuid: string
}

interface RecordSettingsFormGroup {
    workspace: FormControl<string[] | null>
    schema: FormControl<string | null>
    template: FormControl<string | null>
    access: FormControl<string | null>
}

@UntilDestroy()
@Component({
    selector: 'app-record-settings-content',
    templateUrl: './record-settings-content.component.html',
    styleUrl: './record-settings-content.component.sass',
    imports: [
        TranslocoModule,
        ModalLayoutComponent,
        ReactiveFormsModule,
        TbSelectComponent,
        TbOptionComponent,
        EnvDirective,
        AccessSelectorComponent,
        TbButtonComponent,
        ModalLayoutButtonsComponent,
        TbIconComponent,
        TbTooltipComponent,
    ],
})
export class RecordSettingsContentComponent
    extends ModalContainerComponent<RecordSettingsData>
    implements OnInit
{
    protected readonly schemaIconByName = schemaIconByName

    settingsFormGroup!: FormGroup<RecordSettingsFormGroup>
    initialWorkspacesGuid!: string[]
    initialSchemaGuid!: string

    globalWorkspace!: Workspace
    availableWorkspaces!: Workspace[]

    isDirty = false

    ngOnInit() {
        this.initialWorkspacesGuid = this.data.selectedWorkspacesGuid
        this.initialSchemaGuid = this.data.selectedSchemaGuid

        this.globalWorkspace = this.data.workspaces.find((workspace) =>
            isWorkspaceGlobal(workspace),
        )!
        this.availableWorkspaces = this.data.workspaces.filter(
            (workspace) => workspace.guid !== this.globalWorkspace.guid,
        )

        this.settingsFormGroup = new FormGroup({
            workspace: new FormControl(this.initialWorkspacesGuid),
            schema: new FormControl(this.initialSchemaGuid),
            template: new FormControl(''),
            access: new FormControl(''),
        })

        dirtyCheck(this.settingsFormGroup, of(this.settingsFormGroup.value))
            .pipe(untilDestroyed(this))
            .subscribe((isDirty) => (this.isDirty = isDirty))
    }

    cancelSettingsChange() {
        if (!this.hasChanged()) {
            this.modalManagerService.close()
            return
        }

        this.openConfirmationDialog()
    }

    openConfirmationDialog() {
        this.confirmationDialogService
            .openCommon({
                translations: {
                    title: 'confirmation_dialog.discard_changes',
                    message: 'confirmation_dialog.unsaved_data_message',
                    cancel: 'confirmation_dialog.close_anyway',
                    confirm: 'confirmation_dialog.keep_editing',
                },
            })
            .subscribe((result) => {
                if (result === CommonDialogResultStatus.CANCEL) {
                    this.modalManagerService.close()
                }
            })
    }

    applySettingsChange() {
        this.confirmationDialogService
            .openCommon({
                translations: {
                    title: 'confirmation_dialog.continue_with_changes',
                    message: 'confirmation_dialog.settings_confirmation_message',
                    cancel: 'confirmation_dialog.proceed_anyway',
                    confirm: 'confirmation_dialog.keep_editing',
                },
            })
            .subscribe((result) => {
                if (result === CommonDialogResultStatus.CANCEL) {
                    this.modalManagerService.close({
                        workspaceGuids: this.settingsFormGroup.value.workspace,
                        schemaGuid: this.settingsFormGroup.value.schema,
                    })
                }
            })
    }

    hasChanged(): boolean {
        return this.isDirty
    }
}
